import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Edit2, Shield, Trash2, ChevronDown, ChevronRight, Check } from 'lucide-react';
import { api } from '../lib/api';
import { Button, Input, Card, CardContent, Table, THead, TH, TR, TD, Badge, Dialog } from '../components/ui';
export default function Roles() {
    const qc = useQueryClient();
    const { data: roles, isLoading } = useQuery({
        queryKey: ['roles'],
        queryFn: async () => (await api('/api/admin/roles')).data,
    });
    const { data: routes } = useQuery({
        queryKey: ['routes-list'],
        queryFn: async () => (await api('/api/admin/routes')).data,
    });
    // Create Role state
    const [showCreate, setShowCreate] = useState(false);
    const [createName, setCreateName] = useState('');
    const [createSlug, setCreateSlug] = useState('');
    const [createDesc, setCreateDesc] = useState('');
    const [creating, setCreating] = useState(false);
    // Edit Role state
    const [editRole, setEditRole] = useState(null);
    const [editName, setEditName] = useState('');
    const [editSlug, setEditSlug] = useState('');
    const [editDesc, setEditDesc] = useState('');
    const [editing, setEditing] = useState(false);
    // Permissions state
    const [permRole, setPermRole] = useState(null);
    const [permRouteIds, setPermRouteIds] = useState(new Set());
    const [savingPerms, setSavingPerms] = useState(false);
    const [loadingPerms, setLoadingPerms] = useState(false);
    // Auto-generate slug from name
    function toSlug(name) {
        return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }
    // ─── Create Role ──────────────────────────────────────────────────────────
    async function handleCreate(e) {
        e.preventDefault();
        setCreating(true);
        try {
            await api('/api/admin/roles', {
                method: 'POST',
                body: JSON.stringify({ name: createName, slug: createSlug, description: createDesc || undefined }),
            });
            toast.success('Role created');
            setShowCreate(false);
            setCreateName('');
            setCreateSlug('');
            setCreateDesc('');
            qc.invalidateQueries({ queryKey: ['roles'] });
        }
        catch (err) {
            toast.error(err.message);
        }
        finally {
            setCreating(false);
        }
    }
    // ─── Edit Role ────────────────────────────────────────────────────────────
    function openEdit(role) {
        setEditRole(role);
        setEditName(role.name);
        setEditSlug(role.slug);
        setEditDesc(role.description ?? '');
    }
    async function handleEdit(e) {
        e.preventDefault();
        if (!editRole)
            return;
        setEditing(true);
        try {
            await api(`/api/admin/roles/${editRole.id}`, {
                method: 'PATCH',
                body: JSON.stringify({ name: editName, description: editDesc || undefined }),
            });
            toast.success('Role updated');
            setEditRole(null);
            qc.invalidateQueries({ queryKey: ['roles'] });
        }
        catch (err) {
            toast.error(err.message);
        }
        finally {
            setEditing(false);
        }
    }
    // ─── Delete Role ──────────────────────────────────────────────────────────
    async function deleteRole(role) {
        if (role.isSystem) {
            toast.error('Cannot delete system role');
            return;
        }
        if (!confirm(`Delete role "${role.name}"? This will remove all permissions.`))
            return;
        try {
            await api(`/api/admin/roles/${role.id}`, { method: 'DELETE' });
            toast.success('Role deleted');
            qc.invalidateQueries({ queryKey: ['roles'] });
        }
        catch (err) {
            toast.error(err.message);
        }
    }
    // ─── Permissions ──────────────────────────────────────────────────────────
    async function openPermissions(role) {
        setLoadingPerms(true);
        try {
            const detail = await api(`/api/admin/roles/${role.id}`);
            setPermRole(detail);
            setPermRouteIds(new Set(detail.routeIds));
        }
        catch (err) {
            toast.error(err.message);
        }
        finally {
            setLoadingPerms(false);
        }
    }
    function toggleRoute(routeId) {
        setPermRouteIds((prev) => {
            const next = new Set(prev);
            if (next.has(routeId))
                next.delete(routeId);
            else
                next.add(routeId);
            return next;
        });
    }
    function toggleAllRouteType(routeType) {
        if (!routes)
            return;
        const typeRouteIds = routes.filter((r) => r.routeType === routeType).map((r) => r.id);
        const allChecked = typeRouteIds.every((id) => permRouteIds.has(id));
        setPermRouteIds((prev) => {
            const next = new Set(prev);
            for (const id of typeRouteIds) {
                if (allChecked)
                    next.delete(id);
                else
                    next.add(id);
            }
            return next;
        });
    }
    async function savePermissions() {
        if (!permRole)
            return;
        setSavingPerms(true);
        try {
            await api(`/api/admin/roles/${permRole.id}/permissions`, {
                method: 'PUT',
                body: JSON.stringify({ routeIds: Array.from(permRouteIds) }),
            });
            toast.success('Permissions saved');
            setPermRole(null);
        }
        catch (err) {
            toast.error(err.message);
        }
        finally {
            setSavingPerms(false);
        }
    }
    // Group routes by routeType for the permissions editor
    const routeGroups = routes?.reduce((acc, r) => {
        (acc[r.routeType] ??= []).push(r);
        return acc;
    }, {}) ?? {};
    const routeTypeLabels = {
        web_page: 'Web Pages (SSR)',
        web_api: 'Web API',
        mobile_api: 'Mobile API',
        admin_page: 'Admin Pages',
        admin_api: 'Admin API',
        platform_page: 'Platform Dashboard Pages',
        platform_api: 'Platform Dashboard API',
        task: 'Tasks',
        webhook: 'Webhooks',
    };
    return (_jsxs("div", { children: [_jsxs("div", { className: "flex flex-wrap items-center justify-between gap-3 mb-6", children: [_jsx("h1", { className: "text-2xl font-extrabold tracking-tight text-slate-900", children: "Roles & Permissions" }), _jsxs(Button, { onClick: () => setShowCreate(true), children: [_jsx(Plus, { className: "h-4 w-4 mr-2" }), " New Role"] })] }), _jsx(Card, { children: _jsx(CardContent, { className: "p-0", children: _jsxs(Table, { children: [_jsx(THead, { children: _jsxs("tr", { children: [_jsx(TH, { children: "Name" }), _jsx(TH, { children: "Slug" }), _jsx(TH, { children: "Description" }), _jsx(TH, { children: "Type" }), _jsx(TH, { children: "Actions" })] }) }), _jsxs("tbody", { children: [isLoading && _jsx("tr", { children: _jsx(TD, { className: "py-8 text-center text-slate-400", children: "Loading..." }) }), roles?.map((r) => (_jsxs(TR, { children: [_jsx(TD, { className: "font-medium", children: r.name }), _jsx(TD, { children: _jsx("code", { className: "text-xs bg-slate-100 px-1.5 py-0.5 rounded", children: r.slug }) }), _jsx(TD, { className: "text-slate-500", children: r.description ?? '—' }), _jsx(TD, { children: r.isSystem ? _jsx(Badge, { variant: "warning", children: "System" }) : _jsx(Badge, { variant: "brand", children: "Custom" }) }), _jsx(TD, { children: _jsxs("div", { className: "flex gap-1", children: [_jsx(Button, { variant: "ghost", size: "sm", onClick: () => openEdit(r), title: "Edit role", children: _jsx(Edit2, { className: "h-4 w-4" }) }), _jsx(Button, { variant: "ghost", size: "sm", onClick: () => openPermissions(r), title: "Manage permissions", children: _jsx(Shield, { className: "h-4 w-4" }) }), !r.isSystem && (_jsx(Button, { variant: "ghost", size: "sm", onClick: () => deleteRole(r), title: "Delete role", children: _jsx(Trash2, { className: "h-4 w-4 text-red-500" }) }))] }) })] }, r.id))), !isLoading && roles?.length === 0 && _jsx("tr", { children: _jsx(TD, { className: "text-center py-8 text-slate-400", children: "No roles found" }) })] })] }) }) }), _jsx(Dialog, { open: showCreate, onClose: () => setShowCreate(false), title: "Create New Role", children: _jsxs("form", { onSubmit: handleCreate, className: "space-y-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-slate-700 mb-1.5", children: "Name" }), _jsx(Input, { value: createName, onChange: (e) => { setCreateName(e.target.value); setCreateSlug(toSlug(e.target.value)); }, required: true, placeholder: "e.g. Editor" })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-slate-700 mb-1.5", children: "Slug" }), _jsx(Input, { value: createSlug, onChange: (e) => setCreateSlug(e.target.value), required: true, pattern: "[a-z0-9\\-]+", placeholder: "e.g. editor" }), _jsx("p", { className: "mt-1 text-xs text-slate-400", children: "Lowercase, numbers, and dashes only. Used in API routes." })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-slate-700 mb-1.5", children: "Description (optional)" }), _jsx(Input, { value: createDesc, onChange: (e) => setCreateDesc(e.target.value), placeholder: "Brief description of this role" })] }), _jsxs("div", { className: "flex gap-3 pt-2", children: [_jsx(Button, { type: "button", variant: "outline", onClick: () => setShowCreate(false), className: "flex-1", children: "Cancel" }), _jsx(Button, { type: "submit", className: "flex-1", disabled: creating, children: creating ? 'Creating...' : 'Create Role' })] })] }) }), _jsx(Dialog, { open: !!editRole, onClose: () => setEditRole(null), title: "Edit Role", children: _jsxs("form", { onSubmit: handleEdit, className: "space-y-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-slate-700 mb-1.5", children: "Name" }), _jsx(Input, { value: editName, onChange: (e) => setEditName(e.target.value), required: true })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-slate-700 mb-1.5", children: "Slug" }), _jsx(Input, { value: editSlug, disabled: true, className: "bg-slate-50" }), _jsx("p", { className: "mt-1 text-xs text-slate-400", children: "Slug cannot be changed after creation." })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium text-slate-700 mb-1.5", children: "Description" }), _jsx(Input, { value: editDesc, onChange: (e) => setEditDesc(e.target.value), placeholder: "Brief description" })] }), _jsxs("div", { className: "flex gap-3 pt-2", children: [_jsx(Button, { type: "button", variant: "outline", onClick: () => setEditRole(null), className: "flex-1", children: "Cancel" }), _jsx(Button, { type: "submit", className: "flex-1", disabled: editing, children: editing ? 'Saving...' : 'Save Changes' })] })] }) }), _jsx(Dialog, { open: !!permRole, onClose: () => setPermRole(null), title: `Permissions — ${permRole?.name ?? ''}`, children: loadingPerms ? (_jsx("p", { className: "text-center text-slate-400 py-8", children: "Loading permissions..." })) : (_jsxs("div", { className: "space-y-4", children: [_jsx("p", { className: "text-sm text-slate-500", children: "Select which routes this role can access." }), Object.entries(routeGroups).map(([routeType, groupRoutes]) => {
                            const typeRouteIds = groupRoutes.map((r) => r.id);
                            const checkedCount = typeRouteIds.filter((id) => permRouteIds.has(id)).length;
                            const allChecked = checkedCount === typeRouteIds.length;
                            const someChecked = checkedCount > 0 && !allChecked;
                            return (_jsxs("div", { className: "border border-slate-200 rounded-lg overflow-hidden", children: [_jsxs("button", { type: "button", className: "flex items-center justify-between w-full px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left", onClick: () => toggleAllRouteType(routeType), children: [_jsxs("div", { className: "flex items-center gap-2", children: [someChecked ? _jsx(ChevronDown, { className: "h-4 w-4 text-slate-400" }) : allChecked ? _jsx(Check, { className: "h-4 w-4 text-brand-600" }) : _jsx(ChevronRight, { className: "h-4 w-4 text-slate-400" }), _jsx("span", { className: "text-sm font-semibold text-slate-700", children: routeTypeLabels[routeType] ?? routeType }), _jsxs(Badge, { variant: allChecked ? 'brand' : someChecked ? 'warning' : 'default', children: [checkedCount, "/", typeRouteIds.length] })] }), _jsx("span", { className: "text-xs text-slate-400", children: "Click to toggle all" })] }), _jsx("div", { className: "divide-y divide-slate-100", children: groupRoutes.map((route) => (_jsxs("label", { className: "flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 cursor-pointer transition-colors", children: [_jsx("input", { type: "checkbox", checked: permRouteIds.has(route.id), onChange: () => toggleRoute(route.id), className: "h-4 w-4 rounded border-slate-300" }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("code", { className: "text-xs bg-slate-100 px-1.5 py-0.5 rounded font-mono", children: route.method }), _jsx("span", { className: "text-sm text-slate-900 truncate", children: route.path })] }), route.menuLabel && _jsx("span", { className: "text-xs text-slate-400", children: route.menuLabel })] }), route.requireAuth && _jsx(Badge, { variant: "warning", className: "text-[10px]", children: "Auth" })] }, route.id))) })] }, routeType));
                        }), _jsxs("div", { className: "flex gap-3 pt-2", children: [_jsx(Button, { variant: "outline", onClick: () => setPermRole(null), className: "flex-1", children: "Cancel" }), _jsx(Button, { onClick: savePermissions, className: "flex-1", disabled: savingPerms, children: savingPerms ? 'Saving...' : 'Save Permissions' })] })] })) })] }));
}
